/**
 * LegacyPaymentGateway
 *
 * Handle listing activation for free listings
 *
 *
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useQueryClient } from '@zg-rentals/react-query5';

import PropertiesActions from '../../../../actions/PropertiesActions';
import { useMultiplexListingFlow } from '../../../../hooks/queries/multiplex';
import { useActivateListing } from '../../../../hooks/queries/useActivateListing';
import { useSelector } from '../../../../selectors/useSelector';

import ChargeAndActivateListing from '../../../../model/ChargeAndActivateListing';
import PayAndPublishCSEvents from '../../../../model/ClickstreamEvent/PayAndPublishCSEvents';

import FreemiumErrorModal from '../../../Modal/FreemiumErrorModal';

import { trackGtmEvent } from '../../../../../client/analytics';
import { ZRM_GTM_EVENTS } from '../../../../../client/analytics/gtmEvents';

import type { ChargeAndActivateListingProps } from '../../../../model/ChargeAndActivateListing';
import type { FreemiumDatarouterError } from '../../../Modal/FreemiumErrorModal';

type Props = {
  displayPaymentGateway: boolean;
  updateDisplayPaymentGateway: (arg: boolean) => void;
  onChargeAndActivateListingResponse: (arg: { success: boolean; response: ChargeAndActivateListingProps }) => void;
};

const LegacyPaymentGateway = ({
  displayPaymentGateway = false,
  updateDisplayPaymentGateway,
  onChargeAndActivateListingResponse,
}: Props) => {
  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const {
    properties: {
      currentProperty,
      currentProperty: { listingAlias, active },
    },
    user,
  } = useSelector((state) => state);

  const [hasError, setHasError] = useState<{
    display: boolean;
    error?: FreemiumDatarouterError;
  }>({
    display: false,
  });

  const { mutate: activate } = useActivateListing();

  useEffect(() => {
    if (displayPaymentGateway && !active && listingAlias) {
      activateListing();
    }
  }, [displayPaymentGateway, active, listingAlias]);

  const activateListing = () => {
    return activate(
      { getParams: { listingAlias } },
      {
        onSuccess: (response) => {
          trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });

          PayAndPublishCSEvents.ActivateListing({ currentProperty, user });

          updateDisplayPaymentGateway(false);
          onChargeAndActivateListingResponse({
            success: true,
            response: ChargeAndActivateListing({
              activation: {
                dateActivatedTimestamp: response?.activatedMs,
                dateUpdatedTimestamp: response?.updatedMs,
                expirationDateTimestamp: response?.expirationMs,
                ...response,
              },
            }),
          });

          dispatch(
            PropertiesActions.updatePostActivationState({
              daysExtended: response?.daysExtended,
              displayPostActivationModal: isInMultiplexListingFlow ? false : true,
            }),
          );

          queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
        },
        onError: (error) => {
          setHasError({
            display: true,
            error: error as FreemiumDatarouterError,
          });
        },
      },
    );
  };

  return (
    <div data-testid="LegacyPaymentGateway">
      {hasError.display ? (
        <FreemiumErrorModal
          error={hasError.error}
          onClose={() => setHasError({ display: false })}
          onTryAgainClick={activateListing}
          onPublishForFreeClick={activateListing}
        />
      ) : null}
    </div>
  );
};

export default LegacyPaymentGateway;
